import { GetterTree } from 'vuex'
import { RootState } from '@/store'
import { ReferralsState } from '@/store/referrals'
import { isArray, isEqual, pickBy } from 'lodash'
import moment from 'moment'
import { ReferralStatus } from '@/api/types'
import { CompanyAPI } from '@/api/backend-client'
import { Pipeline } from '@/api/model/pipeline'
import { PipelinePhase } from '@/api/model/pipeline-phase'

const formatSpecificDate = (date: Date) => {
    if (date === null) {
        return null
    }

    return moment(date).format('YYYY-MM-DD')
}

const findPhase = (gettersArg: any, id: string): PipelinePhase|undefined => {
    return gettersArg.selectablePipelines
        .flatMap(pipeline => pipeline.phases)
        .find(phase => phase.id === id)
}

const findPipelineForPhase = (gettersArg: any, id: string): Pipeline|undefined => {
    return gettersArg.selectablePipelines.find(pipeline => {
        return pipeline.phases.findIndex(phase => phase.id === id) >= 0
    })
}

export const getters: GetterTree<ReferralsState, RootState> = {
    requestParams(state: ReferralsState): any {
        let status: string|string[] = state.statusFilter
        if (status === null) {
            status = [
                ReferralStatus.NEW,
                ReferralStatus.APPOINTMENT,
                ReferralStatus.PROPOSAL,
                ReferralStatus.ON_HOLD,
                ReferralStatus.PENDING,
                ReferralStatus.UNREACHABLE,
            ]
        }

        const params = {
            pagination: true,
            page: state.page,
            itemsPerPage: state.itemsPerPage,
            search: state.search,
            view: state.direction ? `${state.view}_${state.direction}` : state.view,
            priority: state.priorityFilter,
            type: state.typeFilter,
            status,
            statusReason: state.statusReasonFilter,
            newStatusReason: state.newStatusReasonFilter.map(statusReason => statusReason.id),
            referredBy: state.referredByFilter.map(user => user.id),
            referredByBranch: state.referredByBranchFilter.map(branch => branch.id),
            assignedTo: state.assignedToFilter.map(user => user.id),
            assignedToBranch: state.assignedToBranchFilter.map(branch => branch.id),
            channel: state.channelFilter,
            newSource: state.newSourceFilter.map(source => source.id),
            score: state.scoreFilter,
            day: formatSpecificDate(state.dateSpecificFilter),
            range: state.dateRangeFilter === 'all' ? null : state.dateRangeFilter,
            age: state.ageFilter,
        }

        if (state.orderField && state.orderDirection) {
            params[`order[${state.orderField}]`] = state.orderDirection
        }

        if (CompanyAPI.currentCompany?.pipelinesEnabled) {

            // prevent legacy code above from setting array of status
            if (state.statusFilter === null) {
                params[`status`] = null
            }

            params[`type`] = null
            params[`pipeline`] = state.pipelineFilter
            params[`phase`] = state.phaseFilter?.id
            params[`phase.category`] = state.phaseCategoryFilter
        }

        if (state.ageFilter) {
            params[`range`] = null
        }

        return pickBy(params, (v: any) => {
            if (isArray(v)) {
                return v.length > 0
            }

            return v !== null && v !== undefined
        })
    },

    activeFilterCount(state: ReferralsState): number {
        let count = 0

        Object.keys(state.snapshot).forEach(key => {
            if (!isEqual(state[key], state.snapshot[key])) {
                count++
            }
        })

        return count
    },

    activeFilters(state: ReferralsState): string[] {
        const filters = []

        Object.keys(state.snapshot).forEach(key => {
            if (!isEqual(state[key], state.snapshot[key])) {
                filters.push(`${key} = ${state[key]}`)
            }
        })

        return filters
    },

    activeView(state: ReferralsState): string {
        return state.view
    },

    activeDirection(state: ReferralsState): string {
        return state.direction
    },

    otherTabsWithResults(state: ReferralsState): Array<{ status: string, count: number }> {
        const counts = {
            'all':         state.referralCounts.statusAll,
            'new':         state.referralCounts.statusNew,
            'appointment': state.referralCounts.statusAppointment,
            'proposal':    state.referralCounts.statusProposal,
            'on-hold':     state.referralCounts.statusOnHold,
            'pending':     state.referralCounts.statusPending,
            'unreachable': state.referralCounts.statusUnreachable,
            'successful':  state.referralCounts.statusSuccessful,
            'processed':   state.referralCounts.statusProcessed,
            'closed':      state.referralCounts.statusClosed,
        }

        delete counts[state.statusFilter]
        return Object.entries(counts)
            .filter(([_, value]) => value > 0)
            .map(([status, count]) => {
                return { status, count }
            })
    },

    selectablePipelines(state: ReferralsState): Pipeline[] {
        let pipelines = state.pipelines

        if (state.direction === 'out') {
            pipelines = []
                .concat(state.pipelines, state.outgoingPipelines)
                .sort((a, b) => a.name.localeCompare(b.name))
        }

        return pipelines
    },

    selectedPipeline(state: ReferralsState, gettersArg: any): Pipeline|null {
        if (!state.pipelineFilter) {
            return null
        }

        return gettersArg.selectablePipelines.find(pipeline => pipeline.id === state.pipelineFilter)
    },




    // New search alternatives

    resultsInOtherPipeline(state: ReferralsState, gettersArg: any): Array<{ title: string, count: number; filters: {} }> {
        return state.referralStats.countsPerPhase
            .map(entry => {
                const pipeline = findPipelineForPhase(gettersArg, entry.phase)
                const phase = findPhase(gettersArg, entry.phase)

                // skip if no data
                if (!pipeline || !phase) {
                    return null
                }

                // skip if same pipeline
                if (pipeline.id === state.pipelineFilter) {
                    return null
                }

                return {
                    title: `${phase.name} in ${pipeline.name}`,
                    count: entry.count,
                    filters: {
                        view: state.view,
                        direction: state.direction,
                        phaseCategoryFilter: state.phaseCategoryFilter,
                        pipelineFilter: pipeline.id,
                        phaseFilter: phase,
                    },
                }
            })
            .filter(entry => entry !== null)
    },

    resultsInOtherPhase(state: ReferralsState, gettersArg: any): Array<{ title: string, count: number; filters: {} }> {
        return state.referralStats.countsPerPhase
            .map(entry => {
                const pipeline = findPipelineForPhase(gettersArg, entry.phase)
                const phase = findPhase(gettersArg, entry.phase)

                // skip if no data
                if (!pipeline || !phase) {
                    return null
                }

                // skip if different pipeline
                if (pipeline.id !== state.pipelineFilter) {
                    return null
                }

                return {
                    title: `${phase.name} in ${pipeline.name}`,
                    count: entry.count,
                    filters: {
                        view: state.view,
                        direction: state.direction,
                        phaseCategoryFilter: state.phaseCategoryFilter,
                        pipelineFilter: pipeline.id,
                        phaseFilter: phase,
                    },
                }
            })
            .filter(entry => entry !== null)
    },


    resultsInOtherStatus(state: ReferralsState): Array<{ title: string, count: number; filters: {} }> {
        return state.referralStats.countsPerStatus
            .map(entry => {

                // We dont show results for status open, that would be silly.
                if (entry.status === ReferralStatus.OPEN) {
                    return null
                }

                return {
                    title: `referral.status.${entry.status}`,
                    count: entry.count,
                    filters: {
                        view: state.view,
                        direction: state.direction,
                        phaseCategoryFilter: state.phaseCategoryFilter,
                        pipelineFilter: state.pipelineFilter,
                        phaseFilter: null,
                        statusFilter: entry.status,
                    },
                }
            })
            .filter(entry => entry !== null)
    },
}
